import { ui } from '@/constants';
import { getPreviousSiblings } from '@/helpers';
import { MBV2Error, MBV2NutritionSummary, MonoMenuTopLevelNutritionDetailsSection, OrderApiException, OrderErrorCode, OrderMeal, OrderMealRequest, PreConfiguredMeal, NutritionFactsContentFragment, MenuItemGroupType } from '@/models';
import { CustomerProfile } from '@/models/customer';
import { Routes } from '@/router';
import { NutritionCalculatorAddToBagEvent, NutritionLinkClickEvent } from '@/services/analytics/events';
import { AdobeNutritionCalculatorCartAddEvent } from '@/services/analytics/platforms/adobe-analytics/adobe-events';
import { AddToBagWebInteraction } from '@/services/analytics/platforms/adobe-analytics/constants';
import { getAddToBagInteraction } from '@/services/analytics/platforms/adobe-analytics/helpers';
import { AnalyticsMealType, NutritionLinkClickType } from '@/services/analytics/platforms/adobe-analytics/models';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

@Component({
  name: 'CmgNutritionCalculatorMealBuilderBanner',
})
export default class CmgNutritionCalculatorMealBuilderBanner extends Vue {
  nutritionDetails = false;
  windowTop = 0;
  nutritionDetailsOrdering = ['tcal', 'tfat', 'prot', 'carb', 'sodi', 'Vitamins & Minerals'];
  isStartOrder = false;
  nutritionFacts: NutritionFactsContentFragment | null = null;

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get searchIconUrl(): string {
    return this.$appStore.state.cms.images.icons.search.default;
  }

  get mealType(): string {
    return this.$store.state.mealBuilderV2.topLevelMenuItem.title;
  }

  get mealTypeDescription(): string {
    return this.$store.state.mealBuilderV2.topLevelMenuItem.description;
  }

  get currentNutrition() {
    return this.$store.state.mealBuilderV2.nutrition;
  }

  get hasNoIngredientsSelected(): boolean {
    return !(this.currentNutritionValue('tcal') + this.currentNutritionValue('tfat') + this.currentNutritionValue('prot') + this.currentNutritionValue('carb'));
  }

  get toggleHoverText() {
    return this.nutritionDetails ? 'Close' : 'View More';
  }

  currentNutritionValue(key: string, asPercentage: boolean = false) {
    if (asPercentage) {
      const total = this.currentNutrition['tfat'] + this.currentNutrition['prot'] + this.currentNutrition['carb'];

      return total ? Math.round((this.currentNutrition[key]/total)*100) : 0;
    }

    return Math.round(this.currentNutrition[key]);
  }

  isUKNutritionPage(){
    return this.countryCode === 'GB';
  }

  getEnergyLabelByCountry(){
    if(this.countryCode === 'GB') {
      return 'kcal';
    }
    return 'cal'
  }

  getUKFullTitle() {
    return 'ENERGY KCAL'
  }

  get fullFactsLabel() {
    return this.nutritionFacts?.label;
  }

  get fullFactsPath() {
    return this.nutritionFacts?.path;
  }

  get energyUnitByCountry(): string {
    return this.$services.config.getEnergyUnitByCountry();
  }

  get nutritionDetailSections() {
    return this.$services.monoMenu.getNutritionDetailSections().sort((a, b) => this.getNutritionDetailSectionIndex(a) - this.getNutritionDetailSectionIndex(b));
  }

  get isDesktop(): boolean {
    return this.$appStore.state.ui.clientWidth >= ui.breakpoints.desktop;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }
  
  get hasDietaryTagsSet(): boolean {
    return this.customer?.dietaryTags != null && this.customer.dietaryTags.length > 0;
  }

  get dietsAndAllergensSymbol(): string {
    return this.$appStore.state.cms.images.icons.nutritionFilter.default;
  }

  get isCollapsed(): boolean {
    return this.windowTop > 90 && this.nutritionDetails == false;  // Trying to find the sweet spot of when to collapse banner
  }
  
  async mounted() {
    window.addEventListener('scroll', this.onScroll);

    let nutritionFacts = this.$services.config.getNutritionFacts();
    if(nutritionFacts) {
      this.nutritionFacts = nutritionFacts;
    }
    else {
      await this.$services.config.setNutritionFacts();
      this.nutritionFacts = this.$services.config.getNutritionFacts()!;
    }
  }

  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  }

  onScroll() {
    this.windowTop = window.scrollY;

    if (this.windowTop > 90) {
      this.nutritionDetails = false;
    }
  }

  toggleDetails() {
    this.nutritionDetails = !this.nutritionDetails;
  }

  onClickBack() {
    this.$router.push({ name: Routes.NutritionCalculatorPage });
  }

  sendFullNutritionFactsEvent() {
    this.$services.analytics.send(new NutritionLinkClickEvent(NutritionLinkClickType.FullNutritionFacts));
  }

  onClickAllergens() {
    this.$services.analytics.send(new NutritionLinkClickEvent(NutritionLinkClickType.AllergenStatement));
  }

  onSelectTags(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('dietaryFiltersModal', true);
    this.$services.ui.firstFocus();
  }

  getNutritionDetailSectionIndex(section: MonoMenuTopLevelNutritionDetailsSection): number {
    if (section.key) {  
      return this.nutritionDetailsOrdering.indexOf(section.key);
    }

    // Currently, Vitamins & Minerals does not have a key
    return this.nutritionDetailsOrdering.indexOf(section.name);
  }

  getDisclaimerText() {
    let disclaimerText = this.$services.config.getLegalText().nutritionCalculatorBuilderDisclaimer.replace(/\n/g, '<br>');

    if(disclaimerText) {
      const lastIndex = disclaimerText.lastIndexOf('<br>');
      disclaimerText = disclaimerText.substring(0, lastIndex) + disclaimerText.substring(lastIndex + 4);
    }

    return disclaimerText;
  }
  
  async onClickAddToBag(event: any) {
    let linkPosition = this.getLinkPositionOfPCE();
    if (!this.$services.order.getCurrentOrder()) {
      this.isStartOrder = true;
    }

    const nutrition = this.$store.state.mealBuilderV2.nutrition as MBV2NutritionSummary;
    if(this.$appStore.state.mealBuilderV2.topLevelMenuItem?.type === MenuItemGroupType.PreConfigured) {
      this.$services.mealBuilderV2.addPreconfiguredMealToBag().then(res => {
        const preConfiguredMeal = res as PreConfiguredMeal;
        this.$services.bag.setBagVisibility(true);
        Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
        this.$router.push({ name: Routes.NutritionCalculatorPage });
        const order = this.$services.order.getCurrentOrder();
        
        if (order && preConfiguredMeal) {
          this.sendAddToBagEvent(preConfiguredMeal, nutrition, linkPosition || 0)
        }
      });
    } else {
      this.$services.mealBuilderV2.addMealToBag().then(res => {
        this.$services.bag.setBagVisibility(true);
        Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
        this.$router.push({ name: Routes.NutritionCalculatorPage });
        const order = this.$services.order.getCurrentOrder();
        const orderMeal = (res as OrderMealRequest).meal as OrderMeal;

        if (order && orderMeal) {
          this.$services.analytics.send(new NutritionCalculatorAddToBagEvent(orderMeal, AnalyticsMealType.Standard,this.isStartOrder, nutrition));
        }
      }).catch(err => {
        if (!(err as MBV2Error)?.errorText) { //if this isnt an MBV2 error
          if (err instanceof OrderApiException && err.errors[0].errorCode == OrderErrorCode.MaxEntreeLimitExceeded) {
            this.$services.ui.setModalVisibility('maxEntreeLimitModal', true, { errorMessage: err.errors[0].errorData, errorCode: err.errors[0].errorCode });
          } else {
            throw err;
          }
        } else {
          //scroll for missing required items
          document.getElementsByClassName('validation-container')[0].scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest"
          })
          // If there is an unavailable item, focus on the NC builder card for that item
          // Find a better way to do this than match on error text?
          Object.keys(this.$appStore.state.mealBuilderV2.itemErrors).forEach(key => {
            if (this.$appStore.state.mealBuilderV2.itemErrors[key] === 'Update selection to add to bag') {
              this.$services.ui.focusDiv('div[item-id="' + key  + '"]');
            }
          });
        }
      });
    }
  }
  
  private getLinkPositionOfPCE(): number | undefined {
    let linkPosition: number;
    const PCEs = this.$appStore.state.menu.preConfiguredMeals;
    const matchingPCE = PCEs.find(pce => pce.mealId === Object.keys(this.$appStore.state.mealBuilderV2.selectedItems)[0]);
    let element = document.querySelector(`div.groups-container div[item-id='${matchingPCE?.mealId}']`);
    if (element) {
      return getPreviousSiblings(element).length;
    }

    return;
  }

  private sendAddToBagEvent(orderMeal: PreConfiguredMeal, nutrition: MBV2NutritionSummary, linkPosition: number): void {
    if (orderMeal.mealId && orderMeal.mealName) {
      this.$services.analytics.addLinkClickedEventToStore(getAddToBagInteraction(AddToBagWebInteraction, orderMeal.mealName, linkPosition));
    }
    this.$services.analytics.setAddToBagEventToStore(new AdobeNutritionCalculatorCartAddEvent(new NutritionCalculatorAddToBagEvent(orderMeal, AnalyticsMealType.Standard, this.isStartOrder, nutrition), this.$appStore, this.$services));
  } 
}
